import React from 'react';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { CompanyDashboard, OrbitOpportunityItem, OrbitOverview } from '../../types/aai';

/*
 * Phase 5: the clarity report. What the five phases before it add up to.
 *
 * Everything on this page is read from what the company actually gave Orbit.
 * The readiness scores are the same instrument every person answered, so they
 * compare across companies; the opportunities are the ones on file, in the
 * order the Room ranks them; the first one is the domain to prove.
 *
 * Where a number does not exist yet it says so rather than estimating. A
 * report that guesses at value is worth less than one that admits the gap,
 * because the whole promise of the phases before it is that nothing here is
 * an estimate.
 */

const DIMENSION_LABEL: Record<string, string> = {
  literacy: 'AI literacy',
  tool_adoption: 'Tool adoption',
  usage_depth: 'Usage depth',
  workflow: 'Workflow sophistication',
  output_impact: 'Output impact',
};

/* Readiness is scored 0 to 1 and shown out of 5, because a person reads "2.8
   out of 5" and a fraction of one means nothing to them. The stage word beside
   it comes from the server and is not recomputed here. */
const mid = (range: { low: number; high: number }): number => ((range.low + range.high) / 2) * 5;

/* Money bands are written the way people write them: "$40k-$60k / year",
   "$8k to $12k". A band is money only when it carries a currency symbol or a
   k/m suffix, so "2 of 18" and "78% match" stay out of the maths. */
const parseBand = (text: string): number | null => {
  const raw = (text || '').trim();
  if (!raw || raw.includes('%')) return null;
  const hasCurrency = /[$£€]/.test(raw);
  const parts = raw.match(/[\d][\d.,]*\s*[kKmM]?/g);
  if (!parts) return null;
  const numbers: number[] = [];
  parts.forEach((part) => {
    const suffix = part.trim().slice(-1).toLowerCase();
    const digits = Number(part.replace(/[^\d.]/g, ''));
    if (!Number.isFinite(digits)) return;
    if (suffix === 'k') { numbers.push(digits * 1000); return; }
    if (suffix === 'm') { numbers.push(digits * 1000000); return; }
    if (hasCurrency) numbers.push(digits);
  });
  if (numbers.length === 0) return null;
  const total = numbers.reduce((sum, n) => sum + n, 0);
  return total / numbers.length;
};

/* How much of one year's value the build costs. Cheap against the payoff reads
   as feasible; a build that costs a year of value does not. This is the only
   effort signal on file, so it is the only one the chart may use. */
const feasibilityOf = (value: number | null, cost: number | null): number | null => {
  if (!value || value <= 0 || !cost || cost <= 0) return null;
  return Math.max(0, Math.min(1, 1 - cost / value));
};

interface Plotted {
  item: OrbitOpportunityItem;
  index: number;
  value: number;
  feasibility: number;
  /** 0 to 1 against the largest value on the page. */
  vRel: number;
  quadrant: 'Quick win' | 'Big bet' | 'Fill-in' | 'Avoid';
}

const quadrantOf = (vRel: number, feasibility: number): Plotted['quadrant'] => {
  if (vRel >= 0.5 && feasibility >= 0.5) return 'Quick win';
  if (vRel >= 0.5) return 'Big bet';
  if (feasibility >= 0.5) return 'Fill-in';
  return 'Avoid';
};

const QUADRANT_TONE: Record<Plotted['quadrant'], string> = {
  'Quick win': 'rdy', 'Big bet': 'big', 'Fill-in': 'fill', Avoid: 'fill',
};

const money = (n: number): string => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}m`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
};

export const ClarityReport: React.FC<{
  overview: OrbitOverview;
  isPartner: boolean;
  onBack: () => void;
  onOpenOpportunities: () => void;
}> = ({ overview, isPartner, onBack, onOpenOpportunities }) => {
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not read the readiness scores.', []);

  const items: OrbitOpportunityItem[] = overview.opportunities.items || [];
  const priced = items.filter((i) => i.value);
  const dims = dashboard.data?.company.dimensions || null;
  const rows = dims ? Object.entries(dims).map(([key, range]) => ({ key, label: DIMENSION_LABEL[key] || key, score: mid(range) })) : [];
  let score = 0;
  if (rows.length > 0) score = rows.reduce((sum, r) => sum + r.score, 0) / rows.length;
  const maturity = dashboard.data?.company.maturity;

  let verdict = 'Not ready to price. Finish the maps first.';
  let verdictTone = 'hold';
  if (rows.length > 0 && items.length > 0) {
    verdict = 'GO: the foundations hold and the first domain is identified.';
    verdictTone = 'go';
  }
  if (rows.length > 0 && items.length === 0) {
    verdict = 'Readiness is measured. No opportunity has formed yet.';
    verdictTone = 'hold';
  }

  const lighthouse = items[0] || null;
  const scale = items.slice(1, 3);

  /* The portfolio only plots what carries both numbers. An opportunity with a
     value but no build estimate has no place on a feasibility axis, and putting
     it somewhere plausible would be inventing the very thing the chart claims
     to show. Those are named under the chart instead. */
  const measured = items.map((item, index) => {
    const value = parseBand(item.value);
    const cost = parseBand(item.cost);
    return { item, index, value, feasibility: feasibilityOf(value, cost) };
  });
  const maxValue = measured.reduce((top, m) => (m.value && m.value > top ? m.value : top), 0);
  const plotted: Plotted[] = measured
    .filter((m): m is typeof m & { value: number; feasibility: number } => m.value !== null && m.feasibility !== null)
    .map((m) => {
      const vRel = maxValue > 0 ? m.value / maxValue : 0;
      return { item: m.item, index: m.index, value: m.value, feasibility: m.feasibility, vRel, quadrant: quadrantOf(vRel, m.feasibility) };
    });
  const unplotted = measured.filter((m) => m.value === null || m.feasibility === null);

  return (
    <>
      <div className="det-head">
        <button type="button" className="back" onClick={onBack}>← Company</button>
        <h1>The clarity report</h1>
        <span className="pct">Yours to keep · built from your own answers</span>
      </div>
      <div className="rep-wrap">
        <div className="rep-hero">
          <p className="kick">Executive summary</p>
          <h2>{lighthouse ? `Start with ${lighthouse.title.replace(/\.$/, '')}.` : 'Nothing is ready to build yet.'}</h2>
          <div className="row">
            <div className="m">
              <b>{rows.length > 0 ? `${score.toFixed(1)}/5` : 'not yet'}</b>
              <small>AI readiness{maturity && maturity.band ? ` · ${maturity.band}` : ''}</small>
            </div>
            <div className="m">
              <b>{priced.length > 0 ? priced.length : '0'}</b>
              <small>opportunities carrying a value</small>
            </div>
            <div className="m">
              <b>{overview.counts.known}</b>
              <small>facts behind this, each with a source</small>
            </div>
          </div>
          <span className={clsx('verdict', verdictTone)}>{verdict}</span>
        </div>

        <div className="rep-sec">
          <h3>Readiness scorecard</h3>
          <small>Five dimensions, 1 to 5, from the same instrument every person answered. Scores compare across companies because the questions do not change.</small>
          {dashboard.loading && <p className="rep-note">Reading the scores.</p>}
          {rows.length === 0 && !dashboard.loading && (
            <p className="rep-note">
              Nobody has finished their own onboarding yet, so there is nothing to score. Phase 4 fills this in: every person who answers
              adds a reading, and the score appears once enough people have that no individual can be picked out of it.
            </p>
          )}
          {rows.map((r) => (
            <div key={r.key} className="score-row">
              <span>{r.label}</span>
              <div className="score-bar"><b style={{ width: `${Math.min(100, (r.score / 5) * 100)}%` }} /></div>
              <span className="sc">{r.score.toFixed(1)}</span>
            </div>
          ))}
        </div>

        {plotted.length > 0 && (
          <div className="rep-sec">
            <h3>Opportunity portfolio</h3>
            <small>
              Value against feasibility. Value is the yearly figure someone put on it; feasibility is how little of that first year the
              build costs. Start top right, invest toward top left.
            </small>
            <div className="matrix">
              <span className="qlabel big-bet">Big bet</span>
              <span className="qlabel quick-win">Quick win</span>
              <span className="qlabel avoid">Avoid</span>
              <span className="qlabel fill-in">Fill-in</span>
              <span className="axis-y">Value</span>
              {plotted.map((p) => (
                <button
                  type="button"
                  key={`${p.item.kind}:${p.item.id}`}
                  className={clsx('dot', QUADRANT_TONE[p.quadrant])}
                  style={{ left: `${8 + p.feasibility * 84}%`, top: `${8 + (1 - p.vRel) * 84}%` }}
                  title={`${p.item.title} · ${p.quadrant} · ${money(p.value)} a year`}
                  onClick={onOpenOpportunities}
                >
                  {p.index + 1}
                </button>
              ))}
            </div>
            <div className="axis-x">Feasibility</div>
            {unplotted.length > 0 && (
              <p className="rep-note">
                {unplotted.length} of the {items.length} are not on the chart: {unplotted.map((m) => m.item.title).slice(0, 3).join(', ')}
                {unplotted.length > 3 ? ' and others' : ''}. An opportunity needs both a yearly value and a build estimate before it can
                be placed, and guessing either would be inventing the thing the chart exists to show.
              </p>
            )}
          </div>
        )}

        <div className="rep-sec">
          <h3>Opportunities</h3>
          <small>Everything Orbit found, in the order the Room ranks it. Value is shown only where a person set a number.</small>
          {items.length === 0 && (
            <p className="rep-note">Nothing has formed yet. Opportunities appear as the maps fill; they are never invented from the outside.</p>
          )}
          {items.length > 0 && (
            <div className="opp-table">
              <div className="opp-tr">
                <span>Opportunity</span><span>Quadrant</span><span>Value / yr</span><span>Feasibility</span><span>State</span>
              </div>
              {items.map((item, i) => {
                const spot = plotted.find((p) => p.index === i);
                return (
                  <div key={`${item.kind}:${item.id}`} className="opp-tr">
                    <strong>{i + 1} · {item.title}</strong>
                    <span className={clsx('q', spot && QUADRANT_TONE[spot.quadrant])}>{spot ? spot.quadrant : item.level}</span>
                    <span className="val">{item.value || 'not priced'}{item.cost ? <small>build {item.cost}</small> : null}</span>
                    <span className="q">{spot ? `${(spot.feasibility * 5).toFixed(1)}/5` : 'needs a build estimate'}</span>
                    <span className="q">{item.state}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="rep-note">
            Pricing every opportunity in a range, against the baselines set in phases 1 and 2, is the piece still to build. Until it
            lands, this table shows the values people entered themselves and nothing more.
          </p>
        </div>

        {lighthouse && (
          <div className="rep-sec">
            <h3>The roadmap</h3>
            <small>One domain first. Prove it, then scale.</small>
            <div className="road">
              <div className="rc now">
                <b>Now · under 90 days</b>
                <strong>{lighthouse.title}</strong>
                <small>{lighthouse.summary || 'Proven end to end against the baseline before anything else starts.'}</small>
              </div>
              <div className="rc">
                <b>Then</b>
                <strong>{scale.length > 0 ? 'Scale' : 'Nothing queued yet'}</strong>
                <small>{scale.length > 0 ? scale.map((s) => s.title).join(' · ') : 'The next domain is chosen once the first one proves out.'}</small>
              </div>
              <div className="rc">
                <b>Ongoing</b>
                <strong>Portfolio</strong>
                <small>Orbit runs again each quarter: re-read, re-score, promote the next domain.</small>
              </div>
            </div>
          </div>
        )}

        <div className="signoff">
          <div className="txt">
            <strong>{isPartner ? 'Put it in front of the owner.' : 'Accept and start the first domain.'}</strong>
            <p>
              {isPartner
                ? 'Opportunities move to the owner one at a time, and the owner decides. Nothing is built on a report alone.'
                : 'Confirming an opportunity turns it into a project with a scope the delivery team works to. Nothing builds without that.'}
            </p>
          </div>
          <div className="price">
            <button type="button" className="go" onClick={onOpenOpportunities}>Open opportunities</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClarityReport;
